import { toast } from "@/components/common/Toast/Toast"
import {
  useResolveConflict,
  type MemoryActionPermission,
} from "@/features/material-compliance/api/memoryApi"
import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"

export interface UseConflictResolutionArgs {
  surface: string
  targetType?: string
  targetId?: string
  permission?: MemoryActionPermission
  /**
   * How to refresh this HOST'S OWN reads once a resolve lands, win or lose. Not done inside
   * this hook with a bare, argument-less `invalidateQueries()` any more — that refetches
   * every active query in the app, which on the declaration-review surface includes the
   * immutable supplier PDF (`useDeclarationDocument`, excluded everywhere else precisely so
   * a gesture here doesn't redownload it — see `invalidateDeclaration`'s own note). Each
   * host passes its own scoped invalidation instead, the same helper its other mutations use.
   */
  invalidate: () => void
}

/**
 * The state and mutation behind #6b's missing half: a person clicks a side to highlight it,
 * confirms it, and every other side of the same conflict disappears.
 *
 * ONE HOOK, TWO HOST SURFACES. `MemoryAnnotation` (concentration) and
 * `AnswerMemorySuggestion` (questionnaire) both render a conflict and both need the exact
 * same sequence — select, confirm, filter the losers, remember the winner — so the logic
 * lives once here rather than twice with a chance to drift.
 *
 * RETIRED IDS ARE TRACKED HERE, NOT WAITED FOR FROM A REFETCH. The resolve endpoint already
 * names every side it retired in its response (`retiredEntryIds`), so the UI removes them
 * the instant the request succeeds — a reviewer does not sit looking at a contradiction the
 * server has already settled.
 *
 * BOTH OUTCOMES INVALIDATE THE CACHE, not only the one that wins locally. `recorded: false`
 * is not necessarily nothing happening — it is the honest signal that a COLLEAGUE already
 * settled this exact conflict, possibly choosing the other side, and the screen's cached
 * hint has been stale since the moment they did. A silent return there left two symptoms:
 * the click visibly did nothing (no feedback at all), and a later remount of this same
 * component (switching articles and back, say) could still show the already-retired side,
 * because nothing had ever told the query cache the server's answer had moved. SCOPED to the
 * caller's own `invalidate`, not a bare `invalidateQueries()` — that version refetched every
 * active query in the app, PDF included on the declaration-review surface, each time anyone
 * resolved anything.
 *
 * RESOLVING LATCHES, same reasoning as `MemoryRating`: a second click while the mutation is
 * in flight, or after it already landed, must not fire a second resolve against an entry
 * that may no longer be conflicted.
 *
 * THE LATCH DOES NOT EXPIRE ON ITS OWN, which is why `reset` exists. A host component can
 * stay mounted under the same field key across a refetch, and the entry this hook declared
 * a winner can, much later, be drawn into an entirely new, unrelated conflict on that same
 * field. Nothing about THIS hook's state tells it that happened — `resolvedWinnerId` would
 * still match one of the new conflict's sides by id, silently rendering it as already
 * settled and refusing every new select/confirm. The host is the one that can tell the two
 * apart (it sees the current conflict's side ids), so it calls `reset` the moment a side
 * appears that isn't the previous winner or one of its retired siblings.
 */
export function useConflictResolution({
  surface,
  targetType,
  targetId,
  permission,
  invalidate,
}: UseConflictResolutionArgs) {
  const { translate } = useTranslation()
  const resolve = useResolveConflict(permission)
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null)
  const [resolvedWinnerId, setResolvedWinnerId] = useState<string | null>(null)
  const [retiredEntryIds, setRetiredEntryIds] = useState<string[]>([])

  const select = (entryId: string) => {
    if (resolvedWinnerId) return
    setSelectedEntryId((current) => (current === entryId ? null : entryId))
  }

  const confirm = (entryId: string) => {
    if (resolvedWinnerId || resolve.isPending) return
    resolve.mutate(
      { entryId, surface, targetType, targetId },
      {
        onSuccess: (data) => {
          if (data.recorded) {
            setResolvedWinnerId(entryId)
            setRetiredEntryIds(data.retiredEntryIds ?? [])
          } else {
            // Not an error — the ordinary "someone else already settled this" case. Said
            // out loud rather than swallowed, because the click the person just made
            // otherwise looks like it did nothing.
            toast.warning(translate("MC_MEMORY_CONFLICT_ALREADY_SETTLED"))
          }
          invalidate()
        },
        onError: (error) => {
          // A permission denial already has its own silent handling in
          // `useGuardedMutation` — toasting here on top of that would be the second,
          // contradictory message the pattern exists to avoid everywhere else.
          if (isPermissionDenied(error)) return
          toast.error(translate("MC_MEMORY_CONFLICT_RESOLVE_FAILED"))
        },
      },
    )
  }

  const reset = () => {
    setSelectedEntryId(null)
    setResolvedWinnerId(null)
    setRetiredEntryIds([])
  }

  return {
    selectedEntryId,
    select,
    confirm,
    isConfirming: resolve.isPending,
    resolvedWinnerId,
    retiredEntryIds,
    reset,
  }
}
